import { FC, useRef, useState, useEffect } from 'react'
import { Text, Image, LazyVideo, HorizontalGauge } from '@lounge-fe/ui-kit'
import { useInView } from 'framer-motion'
import { type BraLiftProps, type BraLiftType } from './types'
import { useTranslation } from 'react-i18next'
import { Translations } from '@lounge-fe/i18n'

const scaleMap: Record<
  BraLiftType,
  keyof Translations['product']['pushup_bra']
> = {
  'Light Lift': 'light_lift',
  'Medium Lift': 'medium_lift',
  'High Lift': 'high_lift',
}

export const BraLift: FC<BraLiftProps> = (props) => {
  const { title, lift, description } = props || {}
  const { image, video } = props?.media || {}

  const { t } = useTranslation()
  const ref = useRef(null)
  const inView = useInView(ref, { amount: 0 })

  const [hasImageLoaded, setHasImageLoaded] = useState(false)

  useEffect(() => {
    if (inView) {
      setHasImageLoaded(true)
    }
  }, [inView])

  const shouldLoadImage = hasImageLoaded || inView
  const indexOfLift = Object.keys(scaleMap).indexOf(lift)

  return (
    <div
      id="bra-lift"
      className="bg-primary px-6 py-6 md:px-18 md:py-20 sm:wrapper xl:wrapper--size-sm items-center m-auto justify-between flex flex-col md:flex-row gap-x-[10vw]"
      ref={ref}
    >
      <div className="w-full md:basis-1/2">
        {title && (
          <Text variant="title-four" className="mb-8">
            {title}
          </Text>
        )}
        <HorizontalGauge className="mb-6">
          {Object.entries(scaleMap).map(([lift, translationKey], index) => (
            <HorizontalGauge.Bar
              key={lift}
              label={t(`product.pushup_bra.${translationKey}`)}
              className={
                index > indexOfLift
                  ? 'bg-transparent border-l-transparent'
                  : undefined
              }
            />
          ))}
        </HorizontalGauge>
        {description && (
          <Text
            as="div"
            variant="body-sm"
            className="hidden md:block"
            dangerouslySetInnerHTML={{ __html: description }}
          />
        )}
      </div>
      <div className="basis-1/2 aspect-[4/5] w-full">
        {image && shouldLoadImage && (
          <Image
            data={{
              src: image.src,
            }}
            loading="lazy"
            alt={image.alt}
            className="object-cover w-full h-full"
          />
        )}
        {video && <LazyVideo video={video} inView={inView} />}
      </div>
      {description && (
        <Text
          as="div"
          variant="body-sm"
          className="block md:hidden mt-6"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      )}
    </div>
  )
}
